import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import Slice9Button from "../../../../../lib/html5-software-library/src/ranj/components/scale9/Slice9Button";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import Anchor from "../../../../../lib/html5-software-library/src/ranj/layout/Anchor";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import AnimationFactory from "../../../../../lib/html5-software-library/src/ranj/ui/animation/AnimationFactory";
import AnimationOptions from "../../../../../lib/html5-software-library/src/ranj/ui/animation/AnimationOptions";
import AssetLibrary from "../../../core/assetloading/AssetLibrary";
import SoundType from "../../../core/sound/util/SoundType";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import PullContentManager from "../../../ngt/pull/PullContentManager";
import NPCModel from "../../../ngt/scenario/NPCModel";
import ScenarioLibrary from "../../../ngt/scenario/ScenarioLibrary";
import ScenarioPlayerEvent from "../../../ngt/scenario/ScenarioPlayerEvent";
import CharacterLibrary from "../../characters/model/CharacterLibrary";
import CharacterModel from "../../characters/model/CharacterModel";
import DisplayObjectUtil from "../../util/DisplayObjectUtil";
import ChapterHubEvent from "../event/ChapterHubEvent";

export default class ConversationPreviewScreenMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {RevisableLanguagePack} languagePack 
     * @param {CharacterLibrary} characterLibrary 
     * @param {PullContentManager} pullContentManager
     * @param {AssetLibrary} assetLibrary 
     * @param {ScenarioLibrary} scenarioLibrary 
     * @param {NPCModel} npcModel 
     * @param {SoundManager} soundManager 
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, languagePack, characterLibrary, pullContentManager, assetLibrary, scenarioLibrary, npcModel, soundManager) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        this._languagePack = languagePack;
        this._characterLibrary = characterLibrary;
        this._pullContentManager = pullContentManager;
        this._assetLibrary = assetLibrary;
        this._scenarioLibrary = scenarioLibrary;
        this._npcModel = npcModel
        this._soundManager = soundManager;

        this._dialogId = this.getCustomData().dialogId;

        // Get active characters.
        const definedNpcs = this._npcModel.getNPCs();
        /** @type {CharacterModel[]} */
        this._characters = definedNpcs.map(npc => {
            const characterId = npc.getNpcID()?.trim();
            return this._characterLibrary.getCharacterById(characterId);
        });
    }

    initialize() {
        super.initialize();

        this._initializeBackgroundImage();

        this._backButton = this.createSlice9Button(this._target.backButton, this._handleBackButtonClick.bind(this), this._languagePack.getText(`conversationPreview.backButtonLabel`), SoundType.BUTTON_CLICK, Slice9Button.HORIZONTAL);
        this._continueButton = this.createSlice9Button(this._target.continueButton, this._handleContinueButtonClick.bind(this), this._languagePack.getText(`conversationPreview.startConversation`), SoundType.BUTTON_CLICK, Slice9Button.HORIZONTAL);

        this.preventClickThrough();
        this._updateTrackers();
    }

    _initializeBackgroundImage() {
        this._target.backgroundImageContainer.gotoAndStop(0); // Ensures that the children are populated.
        this._target.backgroundImageContainer.removeAllChildren();

        const imageId = this._scenarioLibrary.getCurrentScenario().previewBackgroundImageId;
        const image = this._assetLibrary.getAsset(imageId);
        if (!image) {
            console.warn(`Image with id ${imageId} does not exist. Ignoring request.`);
            return;
        }

        const bitmap = new createjs.Bitmap(image);
        DisplayObjectUtil.anchorDisplayObject(bitmap, Anchor.C);
        this._target.backgroundImageContainer.addChild(bitmap);
    }

    _updateTrackers() {
        for (let i = 0; i < this._characters.length; i++) {
            const character = this._characters[i];

            const panel = this._target[`characterTrackerPanel${i + 1}`];
            panel.profile.nameTextField.text = character.name;

            panel.trustLevelName.text = this._languagePack.getText(`conversationPreview.trustLevelName`);
            panel.groupLevelName.text = this._languagePack.getText(`conversationPreview.groupLevelName`);

            const trust = character.getTrackerById('trust')?.value || 0;
            panel.trustMeter.gotoAndStop(trust * ConversationPreviewScreenMediator.TRACKER_METER_STEPS_TO_FRAMES);

            const groupSpirit = character.getTrackerById('groupSpirit')?.value || 0;
            panel.groupMeter.gotoAndStop(groupSpirit * ConversationPreviewScreenMediator.TRACKER_METER_STEPS_TO_FRAMES);

            this._setImage(`${character.id}NeutralEmotion`, panel.profile.charImageContainer);
        }

        for (let i = this._characters.length; i < 100; i++) {
            const panel = this._target[`characterTrackerPanel${i + 1}`];
            if (!panel) {
                break;
            }

            panel.visible = false;
        }
    }

    _setImage(imageId, imageContainer) {
        imageContainer.removeAllChildren();

        // Get asset, convert to bitmap, set anchor to center, add as child
        const image = this._assetLibrary.getAsset(imageId);
        if (!image) {
            console.warn(`Image is does not exist. You tried: ${imageId}`);
            return;
        }

        const bitmap = new createjs.Bitmap(image);
        DisplayObjectUtil.anchorDisplayObject(bitmap, Anchor.C);
        imageContainer.addChild(bitmap);
    }

    _handleContinueButtonClick() {
        this.disableInteraction();

        this._startConversation();
        this.closeScreen(this._target, {
            animationType: AnimationFactory.FADE_OUT,
            animationOptions: new AnimationOptions(0, 450, createjs.Ease.sineInOut),
        });
    }

    _handleBackButtonClick() {
        this.disableInteraction();
        this.closeScreen(this._target, {
            animationType: AnimationFactory.FADE_OUT,
            animationOptions: new AnimationOptions(0, 450, createjs.Ease.sineInOut),
        });
    }

    _startConversation() {
        this.dispatch(ChapterHubEvent.HIDE);

        const content = this._pullContentManager.getPullContentByID(this._dialogId);
        const contentPlayer = this._pullContentManager.getContentPlayerForContent(content);
        const scenarioPlayerEvent = new ScenarioPlayerEvent(ScenarioPlayerEvent.PUSH_DIALOG_AVAILABLE, content, contentPlayer);
        this.dispatch(scenarioPlayerEvent.type, { data: scenarioPlayerEvent });
    }
}

ConversationPreviewScreenMediator.TRACKER_METER_STEPS_TO_FRAMES = 20; // There are 5 steps in the tracker meter, so 100 / 5 = 20 frames.